<script lang="ts" setup>
import type { IGetAlbumFilters } from "@/api"
import type { INavbarItem, INavbarVariant } from "@/types"
import logo from "@brand-logo"
import footerLogo from "@brand-logo-footer"
import { useMediaQuery, useScrollLock, useUrlSearchParams } from "@vueuse/core"
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap"
import { computed, onUnmounted, reactive, ref, useTemplateRef, watchEffect } from "vue"
import { useRoute } from "vue-router"
import { useGetAlbum, useGetEventVault } from "@/api"
import { EventLinksDrawer, LanguageSelector } from "@/components"
import { getBrandFooterUrls, getBrandName } from "@/components/Footer/footer.constants"
import { AppLink, Button, Icon } from "@/components/ui"
import { useGalleryPermissions, useModal, useTranslation } from "@/composables"
import { ExperienceType } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { EventLinksDrawerKey } from "@/symbols"
import { getGuestNavigationHref } from "@/utils"
import { AlbumIdEventBus } from "./AlbumIdEventBus"

interface NavProps {
  variant?: INavbarVariant
  isHost?: boolean
  isAdmin?: boolean
  isMinimal?: boolean
  hideNavItems?: boolean
  title?: string
  imageSrc?: string
  items?: INavbarItem[]
}

const props = withDefaults(defineProps<NavProps>(), {
  variant: "default",
  isHost: false,
  isAdmin: false,
  isMinimal: false,
  hideNavItems: false,
  title: "",
  imageSrc: "",
  items: () => [],
})

const route = useRoute()

const { t } = useTranslation()
const { toggle: toggleLinksDrawer } = useModal(EventLinksDrawerKey)

const brand = getBrandName()
const brandUrls = getBrandFooterUrls()

const getStartedUrl = computed(() => {
  const url = new URL(brandUrls.getStarted)
  url.searchParams.set("utm_source", "GPX")
  url.searchParams.set("utm_medium", "Event Guest")
  url.searchParams.set("utm_campaign", "Mobile Menu")
  return url.toString()
})

const eventVaultStore = useEventVaultStore()

const urlParams = useUrlSearchParams<{ album?: unknown }>()
const currentAlbumId = ref<number | undefined>(typeof urlParams.album === "string" ? Number(urlParams.album) : undefined)

AlbumIdEventBus.on(id => (currentAlbumId.value = id))

const getAlbumFilters = reactive<IGetAlbumFilters>({
  include: ["settings"],
})

const { data: eventVault, isLoading: isLoadingEventVault } = useGetEventVault(eventVaultStore.eventVaultId)
const { data: currentAlbum, isLoading: isLoadingAlbum } = useGetAlbum(eventVaultStore.eventVaultId, currentAlbumId, getAlbumFilters)

const { isUploadsEnabled, isGalleryEnabled } = useGalleryPermissions(eventVault, currentAlbum, { isHost: () => props.isHost })

const navBarTitle = computed<string | undefined>(() => {
  const albumTitle = currentAlbum.value?.title
  const eventHeader = eventVault.value?.eventHeader ?? eventVault.value?.title
  return props.title || albumTitle || eventHeader
})

const menuRef = useTemplateRef<HTMLElement | null>("menuRef")

function isInsideTeleportedPopperContent(event: MouseEvent | TouchEvent) {
  return event.target instanceof HTMLElement
    && event.target.closest("[data-reka-popper-content-wrapper]") !== null
}

const { deactivate: deactivateFocus, activate: activateFocus } = useFocusTrap(menuRef, {
  allowOutsideClick: isInsideTeleportedPopperContent,
})

const isMenuOpen = ref<boolean>(false)
const isMobileView = useMediaQuery("(max-width: 1024px)")
const isBodyLocked = useScrollLock(document.body)

watchEffect(() => {
  const mobile = isMobileView.value
  const open = isMenuOpen.value

  if (!mobile) {
    isMenuOpen.value = false
  }

  if (open && mobile)
    activateFocus()
  else deactivateFocus()

  isBodyLocked.value = Boolean(open && mobile)
})

onUnmounted(() => {
  isMenuOpen.value = false
  isBodyLocked.value = false
  deactivateFocus()
})

function isActive(page: string) {
  return window.location.pathname.includes(`/guest/${eventVaultStore.eventVaultId}/${page}`)
}

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
}

function getNavItemHref(page: string, additionalParams: Record<string, string> = {}) {
  if (eventVaultStore.eventVaultId) {
    return getGuestNavigationHref(page, eventVaultStore.eventVaultId, currentAlbum.value?.id, additionalParams)
  }

  return "#"
}

function handleCloseDrawer() {
  if (isMenuOpen.value)
    activateFocus()
}

function closeMenu() {
  isMenuOpen.value = false
}

const navItemTabIndex = computed(() => {
  return isMenuOpen.value || !isMobileView.value ? "0" : "-1"
})

const titleLink = computed(() => {
  if (props.hideNavItems) {
    return route.fullPath
  }

  const isSinglePageEvent = eventVault.value?.eventVaultSettings?.experienceType === ExperienceType.SinglePage

  if (isSinglePageEvent) {
    return currentAlbumId.value ? `/guest/${eventVaultStore.eventVaultId}?album=${currentAlbumId.value}` : `/guest/${eventVaultStore.eventVaultId}`
  }

  return getNavItemHref("welcome")
})
</script>

<template>
  <div class="gpx-navbar-wrapper z-40" :class="[`gpx-navbar-wrapper_${variant}`]">
    <nav ref="menuRef" class="gpx-navbar" :class="[`gpx-navbar_${variant}`]">
      <div class="container gpx-container-nav">
        <input id="menuCheckbox" v-model="isMenuOpen" type="checkbox">
        <AppLink
          :to="titleLink"
          :tabindex="!isMenuOpen ? '0' : '-1'"
          class="gpx-brand"
          :class="[`gpx-brand_${variant}`, { 'gpx-brand_hidden-items': hideNavItems }]"
          data-test="nav-brand"
        >
          <img v-if="imageSrc" :src="imageSrc" :alt="t('nav.custom_logo_alt')" loading="lazy">
          <h5 v-else-if="navBarTitle" data-test="nav-event-title">
            {{ navBarTitle }}
          </h5>
          <img v-else :src="logo" :alt="t('footer.logo_alt', { brand })" loading="lazy">
        </AppLink>
        <Button
          v-if="!hideNavItems"
          :tabindex="!isMenuOpen && isMobileView ? '0' : '-1'"
          class="gpx-nav-hamburger-toggle"
          :class="[`gpx-nav-hamburger-toggle_${variant}`]"
          variant="flat"
          @click="toggleMenu"
        >
          <Icon icon="CircleMenu" size="lg" />
        </Button>
        <div v-if="!hideNavItems" class="gpx-nav-items z-50 lg:z-auto">
          <div class="gpx-nav-items-mobile-header">
            <AppLink
              :to="getNavItemHref('welcome')"
              data-test="nav-brand"
              class="gpx-brand-mobile"
              :tabindex="navItemTabIndex"
            >
              <img v-if="imageSrc" :src="imageSrc" :alt="t('nav.custom_logo_alt')" loading="lazy">
              <h3 v-else-if="title && title.length < 8">
                {{ title }}
              </h3>
              <h5 v-else-if="title">
                {{ title }}
              </h5>
            </AppLink>
            <Button
              :tabindex="isMenuOpen ? '0' : '-1'"
              class="gpx-nav-close-toggle"
              variant="flat"
              @click="toggleMenu"
            >
              <span class="sr-only">{{ t("nav.close_menu_sr") }}</span>
              <Icon icon="Close" size="sm" />
            </Button>
          </div>
          <ul v-if="items.length" data-test="nav-items">
            <li
              v-for="(item, index) in items"
              :key="`nav-item-${index}`"
              class="gpx-nav-item"
              :class="[`gpx-nav-item_${variant}`, { 'gpx-nav-item_active': item.active }]"
            >
              <component
                :is="item.href ? AppLink : 'button'"
                :to="item.href"
                :tabindex="navItemTabIndex"
                class="font-bold lg:font-medium"
                @click="item.onClick"
              >
                {{ item.label }}
              </component>
            </li>
            <li
              v-if="isMinimal && !hideNavItems"
              class="gpx-nav-item gpx-nav-item-language"
              :class="`gpx-nav-item_${variant}`"
              data-test="nav-item-language"
            >
              <LanguageSelector
                variant="compact"
                is-navbar
                :activator-tab-index="navItemTabIndex"
              />
            </li>
          </ul>
          <ul v-else data-test="nav-items">
            <li v-if="isHost" class="gpx-nav-item" :class="`gpx-nav-item_${variant}`" data-test="nav-item-host">
              <AppLink
                class="font-bold lg:font-medium"
                :to="eventVaultStore.eventVaultId ? `/event/${eventVaultStore.eventVaultId}` : '/event/dashboard'"
                :tabindex="navItemTabIndex"
                @click="closeMenu"
              >
                {{ t("manage_event") }}
              </AppLink>
            </li>
            <li
              v-if="eventVaultStore.eventVaultId && !isMinimal"
              class="gpx-nav-item"
              :class="[`gpx-nav-item_${variant}`, { 'gpx-nav-item_active': isActive('welcome') }]"
              data-test="nav-item-welcome"
            >
              <AppLink
                class="font-bold lg:font-medium"
                :to="getNavItemHref('welcome')"
                :tabindex="navItemTabIndex"
                @click="closeMenu"
              >
                {{ t("welcome") }}
              </AppLink>
            </li>
            <li
              v-if="isUploadsEnabled && !isMinimal"
              class="gpx-nav-item"
              :class="[`gpx-nav-item_${variant}`, { 'gpx-nav-item_active': isActive('gallery') }]"
              data-test="nav-item-upload"
            >
              <AppLink
                class="font-bold lg:font-medium"
                :to="
                  getNavItemHref(
                    'gallery',
                    Boolean(currentAlbumId) || !eventVault?.eventVaultSettings?.hideMainGallery
                      ? { upload: 'true' }
                      : undefined,
                  )
                "
                :tabindex="navItemTabIndex"
                @click="closeMenu"
              >
                {{ eventVault?.eventVaultSettings?.videoEnabled ? t("add_photos_videos") : t("add_photos") }}
              </AppLink>
            </li>
            <li
              v-if="isGalleryEnabled && !isMinimal"
              class="gpx-nav-item"
              :class="[`gpx-nav-item_${variant}`, { 'gpx-nav-item_active': isActive('gallery') }]"
              data-test="nav-item-gallery"
            >
              <AppLink
                class="font-bold lg:font-medium"
                :to="getNavItemHref('gallery')"
                :tabindex="navItemTabIndex" @click="closeMenu"
              >
                {{ t("gallery") }}
              </AppLink>
            </li>
            <li
              v-if="eventVaultStore.eventVaultId && !eventVault?.eventVaultSettings?.hideGuestbook && !isMinimal"
              class="gpx-nav-item"
              :class="[`gpx-nav-item_${variant}`, { 'gpx-nav-item_active': isActive('messages') }]"
              data-test="nav-item-guestbook"
            >
              <AppLink
                class="font-bold lg:font-medium"
                :to="getNavItemHref('messages')"
                :tabindex="navItemTabIndex"
                @click="closeMenu"
              >
                {{ t("guestbook") }}
              </AppLink>
            </li>
            <li
              v-if="eventVaultStore.eventVaultId && !isMinimal"
              class="gpx-nav-item"
              :class="[`gpx-nav-item_${variant}`, { 'gpx-nav-item_active': isActive('remindmelater') }]"
              data-test="nav-item-remind"
            >
              <AppLink
                class="font-bold lg:font-medium"
                :to="getNavItemHref('reminder')" :tabindex="navItemTabIndex" @click="closeMenu"
              >
                {{ t("set_reminder") }}
              </AppLink>
            </li>
            <li v-if="isHost" class="gpx-nav-item" :class="`gpx-nav-item_${variant}`" data-test="nav-item-share-link">
              <button
                class="font-bold lg:font-medium"
                :tabindex="navItemTabIndex" @click="toggleLinksDrawer"
              >
                {{ currentAlbum ? t("nav.share_album") : t("nav.share_event") }}
              </button>
            </li>
            <li
              class="gpx-nav-item gpx-nav-item-host-login"
              :class="`gpx-nav-item_${variant}`"
              data-test="nav-item-host-login"
            >
              <AppLink
                class="font-bold lg:font-medium"
                to="/login" :tabindex="navItemTabIndex" @click="closeMenu"
              >
                {{ t("host_login") }}
              </AppLink>
            </li>
            <li v-if="isAdmin" class="gpx-nav-item" :class="`gpx-nav-item_${variant}`" data-test="nav-item-admin">
              <a
                class="font-bold lg:font-medium"
                href="/admin"
                :tabindex="navItemTabIndex"
              >
                {{ t("admin") }}
              </a>
            </li>
            <li class="gpx-nav-item lg:hidden" :class="`gpx-nav-item_${variant}`" data-test="nav-item-buy">
              <a
                class="font-bold lg:font-medium"
                :href="getStartedUrl" :tabindex="navItemTabIndex" target="_blank" rel="noopener noreferrer"
              >
                {{ t("footer.get_guestpix") }}
              </a>
            </li>
            <li
              v-if="isMinimal && !hideNavItems"
              class="gpx-nav-item gpx-nav-item-language"
              :class="`gpx-nav-item_${variant}`"
              data-test="nav-item-language"
            >
              <LanguageSelector
                variant="compact"
                is-navbar
                :activator-tab-index="navItemTabIndex"
              />
            </li>
          </ul>
          <div class="gpx-nav-items-mobile-footer">
            <img :src="footerLogo" :alt="t('footer.logo_alt', { brand })" loading="lazy">
          </div>
        </div>
        <slot v-if="!hideNavItems" name="menu" />
      </div>
    </nav>
  </div>
  <EventLinksDrawer
    :is-loading="isLoadingEventVault || isLoadingAlbum"
    :event-vault="currentAlbum ? undefined : eventVault"
    :album="currentAlbum || undefined"
    @open="deactivateFocus"
    @close="handleCloseDrawer"
  />
</template>

<style scoped>
*,
*::after,
*::before {
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}

.gpx-navbar-wrapper {
  left: 0;
  right: 0;
  top: 0;
  border-bottom: var(--template-border);
}

.gpx-navbar-wrapper_transparent {
  border-bottom: none;
}

.gpx-brand {
  color: var(--template-nav-text);
  vertical-align: middle;
  max-height: calc(var(--gpx-navbar-height) - 1.5rem);
  margin-right: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.gpx-brand_hidden-items {
  cursor: default;
}

.gpx-navbar .gpx-brand {
  text-decoration: none !important;
}

.gpx-navbar img {
  max-height: 40px;
}

.gpx-brand h3,
.gpx-brand h5,
.gpx-brand-mobile h3,
.gpx-brand-mobile h5 {
  margin: 0;
  color: var(--template-nav-text);
  vertical-align: middle;
}

.gpx-brand_transparent h3,
.gpx-brand_transparent h5 {
  color: #fff;
}

.gpx-brand h3,
.gpx-brand-mobile h3 {
  font-size: 1rem;
}

.gpx-brand h5,
.gpx-brand-mobile h5 {
  font-size: 1.3rem;
}

.gpx-nav-item a,
.gpx-nav-item button {
  color: var(--template-nav-text, --template-primary-colour);
  text-decoration: none;
  background-color: transparent;
  border: none;
  font-size: 1rem;
}

.gpx-nav-item-language :deep(button) {
  margin-top: 0;
  color: var(--template-nav-text, --template-primary-colour);
}

.gpx-nav-item_transparent.gpx-nav-item-language :deep(button) {
  color: #fff;
}

.gpx-nav-item_active a,
.gpx-nav-item_active button {
  text-decoration: underline;
}

.gpx-nav-item_transparent a,
.gpx-nav-item_transparent button {
  color: #fff;
}

.gpx-nav-item a:hover,
.gpx-nav-item a:focus,
.gpx-nav-item button:hover,
.gpx-nav-item button:focus {
  text-decoration: underline;
  cursor: pointer;
}

.gpx-nav-item_transparent a:hover,
.gpx-nav-item_transparent a:focus,
.gpx-nav-item_transparent button:hover,
.gpx-nav-item_transparent button:focus {
  color: #fff;
}

.gpx-nav-items-mobile-header,
.gpx-nav-items-mobile-footer,
.gpx-nav-item-host-login,
.gpx-nav-hamburger-toggle,
.gpx-container-nav input[type="checkbox"] {
  display: none;
}

.gpx-navbar {
  background-color: var(--template-header-background, #e4d2be);
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
}

.gpx-navbar_transparent {
  background-color: transparent;
  border: none;
}

.gpx-container-nav {
  display: flex;
  gap: 5rem;
  width: 100%;
  align-items: center;
  min-height: var(--gpx-navbar-height);
  margin: 0 auto;
}

.gpx-nav-items ul {
  white-space: nowrap;
  display: flex;
  flex-direction: row;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 1rem;
}

@media (max-width: 1024px) {
  .gpx-container-nav {
    padding: 0 1rem;
    gap: 1.5rem;
  }

  .gpx-nav-items {
    position: absolute;
    top: 0;
    height: 100vh;
    width: 100vw;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    transform: translate(-150%);
    transition: transform 0.5s ease-in-out;
    background-color: var(--template-header-background, #e4d2be);
    overflow: auto;
  }

  input[type="checkbox"]:checked ~ .gpx-nav-items {
    transform: translateX(0);
  }

  .gpx-nav-items ul {
    flex-direction: column;
    height: 100%;
    padding: 1.5rem 1rem;
  }

  .gpx-nav-items-mobile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--template-nav-text, #072a47);
    padding: 0 1rem;
    min-height: 75px;
  }

  .gpx-nav-items-mobile-footer {
    display: block;
    max-height: 40px;
    justify-content: center;
    align-self: center;
    width: auto;
    margin-bottom: 2rem;
  }

  .gpx-nav-close-toggle,
  .gpx-nav-hamburger-toggle {
    color: var(--template-nav-text, #072a47);
  }

  .gpx-nav-hamburger-toggle {
    display: flex;
  }

  .gpx-nav-hamburger-toggle_transparent {
    border: none;
  }

  .gpx-brand,
  .gpx-nav-item-host-login {
    display: block;
  }

  .gpx-navbar .gpx-brand-mobile {
    text-decoration: none !important;
  }

  .gpx-nav-item a,
  .gpx-nav-item button {
    font-size: 1.35rem;
  }

  .gpx-nav-item_transparent a,
  .gpx-nav-item_transparent button {
    color: var(--template-nav-text, #072a47);
  }

  .gpx-nav-item_transparent.gpx-nav-item-language :deep(button) {
    color: var(--template-nav-text, #072a47);
  }

  .gpx-nav-item_transparent a:hover,
  .gpx-nav-item_transparent a:focus,
  .gpx-nav-item_transparent button:hover,
  .gpx-nav-item_transparent button:focus {
    color: var(--template-nav-text, #072a47);
  }

  .gpx-nav-item_transparent.gpx-nav-item-language :deep(button:hover),
  .gpx-nav-item_transparent.gpx-nav-item-language :deep(button:focus) {
    color: var(--template-nav-text, #072a47);
  }
}
</style>
